import styled, { createGlobalStyle } from "styled-components";
import { Modal } from "@/components/modal/modal";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { memoryMonitor } from "./memory-monitor";
import { MemoryWarningBanner } from "./memory-warning-banner";
import { initializeFormHeavyResources } from "./lazy-form-init";
import { useAutoSaveDrafts } from "./draft-auto-save";
import { FullPageLoading } from "@/components/ui/full-page-loading";
import { Post, PostFormState } from "shared/types/post";

import { useDispatch, useSelector } from "react-redux";
import {
  closeBulkCreatePostModal,
  closePostFormInBulkCreate,
  duplicatePostFormInBulkCreate,
  openCreatePostModalV2,
  savePostFormInBulkCreate,
} from "./bulk-create-post.actions";
import { EditPostModal } from "../components/CreatePostModal";
import { ReduxRootState } from "../../../types/redux-store";
import { getOrderedEntities } from "../../../selectors/selectorUtils";
import {
  BulkTabContext,
  ChildCallbacks,
  SaveFormResult,
} from "../../../components/Posts/PostForm/types";
import { checkInstagramPostOverlapping } from "../../../actions/postForm";
import { usePostForm } from "../../../components/Posts/PostForm/use-post-form";
import PostForm from "../../../components/Posts/PostForm/PostForm";
import { PostMiniCardTabs } from "../components/post-mini-card/post-mini-card-tabs";
import { formToMiniCardState } from "../components/post-mini-card/state-conversion";
import {
  getUser,
  getUserPostStatuses,
} from "../../../selectors/commonSelectors";
import { selectedPostIdSlice, useBulkState } from "./bulk-create-post.model";
import { BulkCapacityNotice } from "./bulk-capacity-notice";
import { StatePostSaveSuccess } from "../components/post-save-success/post-save-success";
import { isFormDirty, useChildCallbacks, useHandleFormDirty } from "./hooks";
import { useConfirmDialog } from "./use-confirm-dialog";
import { hotkeyManager } from "../../../ui/SkedCaption/hot-key-manager";
import { useTrackEvent } from "../../../libs/analytics";
import {
  CREATE_POST_BULK_ADD,
  CREATE_POST_BULK_CANCEL,
  CREATE_POST_BULK_DUPLICATE,
  CREATE_POST_LI,
  CREATE_POST_SINGLE_CANCEL,
  CREATE_POST_YT,
  CREATE_SINGLE_POST,
} from "../../../constants/Events";
import { getPostedState } from "../../../utils/posts";
import {
  BulkCreatePostContext,
  useBulkCreatePostContextProvider,
} from "./bulk-create-post.context";

export const BulkCreatePostModal = () => {
  const dispatch = useDispatch();
  const bulkCreatePostContext = useBulkCreatePostContextProvider();
  const pendingState = useBulkState().bulkModalPendingState;
  const pendingJsx =
    pendingState.isLoading || pendingState.error ? (
      <>
        <Modal.Header closeButton={!!pendingState.error}>
          <Modal.Title>Edit Post</Modal.Title>
        </Modal.Header>
        <div className="w-[1224px] h-[748px] flex items-center justify-center relative">
          {pendingState.isLoading && <FullPageLoading />}
          {pendingState.error && (
            <div className="text-red-500">
              Error on Loading Posts, details: {pendingState.error}
            </div>
          )}
        </div>
      </>
    ) : undefined;
  return (
    <BulkCreatePostContext.Provider value={bulkCreatePostContext}>
      <ModalContainer
        title="Bulk Create Posts"
        isLoading={false}
        isHidden={false}
        className="my-4 h-[calc(100vh-32px)] max-h-[calc(100vh-32px)] w-[calc(100vw-32px)] min-w-0 max-w-none overflow-hidden rounded-lg"
        onCancel={() => {
          if (pendingState?.error) {
            dispatch(closeBulkCreatePostModal());
          }
        }}
      >
        {pendingJsx ? pendingJsx : <BulkTabs />}
      </ModalContainer>
    </BulkCreatePostContext.Provider>
  );
};

const BulkTabs = () => {
  const dispatch = useDispatch();

  const forms = useSelector((state: ReduxRootState) =>
    getOrderedEntities(state.forms.postForms)
  );

  const user = useSelector(getUser);
  const userCollabSettings = useSelector(getUserPostStatuses);
  const { selectedPostId, setSelectedPostId } = useSelectedPostId();
  const childrenCallbacksMapRef = useRef<Map<string, ChildCallbacks>>(
    new Map()
  );
  const trackEvent = useTrackEvent();

  // Resilience: if selectedPostId doesn't match any form, auto-select the first one.
  // This prevents a blank modal if form creation and selection get out of sync.
  useEffect(() => {
    if (forms.length > 0 && selectedPostId) {
      const hasMatch = forms.some((f) => f.post._id === selectedPostId);
      if (!hasMatch) {
        setSelectedPostId(forms[0].post._id);
      }
    }
  }, [forms, selectedPostId, setSelectedPostId]);

  // Auto-save drafts to localStorage
  useAutoSaveDrafts();

  // Start/stop memory monitoring
  useEffect(() => {
    memoryMonitor.start();
    return () => {
      memoryMonitor.stop();
    };
  }, []);

  // Update form count for memory monitoring
  useEffect(() => {
    memoryMonitor.setFormCount(forms.length);
  }, [forms.length]);

  // Initialize lazy-loaded forms on selection
  // Track which forms we've initialized to prevent re-initialization on form state updates
  const initializedFormsRef = useRef<Set<string>>(new Set());

  // Cleanup: Remove closed forms from the initialized set to prevent memory leak
  useEffect(() => {
    const currentFormIds = new Set(forms.map((f) => f.post._id));
    const initializedIds = Array.from(initializedFormsRef.current);

    initializedIds.forEach((id) => {
      if (!currentFormIds.has(id)) {
        initializedFormsRef.current.delete(id);
      }
    });
  }, [forms.length]); // Run when form count changes (forms added/removed)

  useEffect(() => {
    if (!selectedPostId) return;

    const selectedForm = forms.find((f) => f.post._id === selectedPostId);
    if (
      selectedForm &&
      selectedForm.isLazyLoaded &&
      !selectedForm.isInitialized &&
      !initializedFormsRef.current.has(selectedPostId)
    ) {
      // Mark as initializing to prevent duplicate calls
      // Note: This happens synchronously before async initialization completes.
      // If user rapidly switches tabs, multiple forms may be marked as "initializing"
      // before their async resources load. This is acceptable because:
      // 1. The Redux isInitialized flag remains the source of truth
      // 2. Re-selecting a form won't re-trigger (prevented by this ref)
      // 3. Image preloading is fire-and-forget (no state corruption)
      initializedFormsRef.current.add(selectedPostId);
      initializeFormHeavyResources(selectedForm, dispatch);
    }
  }, [selectedPostId]); // Only depend on selectedPostId, not forms

  hotkeyManager.useSubmitPostHotkey(() => {
    childrenCallbacksMapRef.current
      ?.get(selectedPostId || "")
      ?.onSubmit?.("save-and-show-success-screen");
  }, [selectedPostId]);

  const isCreation = forms.some((c) => c.requestType === "create");

  const [isSavingAll, setIsSavingAll] = useState(false);
  const savingAllTargetIdsRef = useRef<Set<string>>(new Set());

  // Reset isSavingAll when all targeted forms have saved or been removed.
  // savingAllTargetIdsRef only contains IDs of forms without formSaveData at click time,
  // so a form already having formSaveData here means it saved after the click.
  // A missing form (not in state) counts as resolved — it was removed/closed.
  useEffect(() => {
    if (!isSavingAll) return;
    const targetIds = savingAllTargetIdsRef.current;
    if (targetIds.size === 0) {
      setIsSavingAll(false);
      return;
    }

    const allResolved = [...targetIds].every((id) => {
      const form = forms.find((f) => f.post._id === id);
      return !form || form.formSaveData;
    });

    if (allResolved) {
      savingAllTargetIdsRef.current = new Set();
      setIsSavingAll(false);
    }
  }, [isSavingAll, forms]);

  // Safety timeout — if saves haven't all resolved after 15s, assume some failed.
  // Uses the ref (not forms) to avoid stale closure — the resolution effect above
  // removes IDs from the ref as forms save, so whatever remains is what failed.
  useEffect(() => {
    if (!isSavingAll) return;
    const timer = setTimeout(() => {
      const failedCount = savingAllTargetIdsRef.current.size;
      savingAllTargetIdsRef.current = new Set();
      setIsSavingAll(false);
      if (failedCount > 0) {
        toast.error(
          `${failedCount} post${failedCount > 1 ? "s" : ""} failed to save. Please check for errors and try again.`
        );
      }
    }, 15_000);
    return () => clearTimeout(timer);
  }, [isSavingAll]);

  const { confirm, dialog: confirmDialog } = useConfirmDialog();

  const handleCloseForm = (
    form: PostFormState,
    onConfirmClose?: () => void
  ) => {
    if (isFormDirty(form)) {
      confirm({
        title: "Discard post?",
        message:
          "Are you sure you want to discard this post? Any unsaved work will be lost.",
        confirmText: "Discard post",
        onConfirm: () => {
          dispatch(
            closePostFormInBulkCreate({
              postId: form.post._id,
              actionOnCloseSelected: "select-next",
            })
          );
          onConfirmClose?.();
        },
      });
    } else {
      dispatch(
        closePostFormInBulkCreate({
          postId: form.post._id,
          actionOnCloseSelected: "select-next",
        })
      );
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <MemoryWarningBanner />
      <BulkCapacityNotice />
      <div className="flex min-h-0 flex-1 flex-row overflow-hidden">
        <PostMiniCardTabs
          showAddNewButton={isCreation}
          cards={forms.map((form) =>
            formToMiniCardState({
              form,
              isSelected: selectedPostId === form.post._id,
              user,
              userCollabSettings,
            })
          )}
          onAddNewForm={() => {
            const selectedForm = forms.find(
              (form) => form.post._id === selectedPostId
            );
            dispatch(
              openCreatePostModalV2({
                accountIds: selectedForm?.accounts?.map((acc) => acc._id) || [],
                media: { type: "skip-media" },
              })
            );
            trackEvent({
              eventName: CREATE_POST_BULK_ADD,
            });
          }}
          onSelect={(card) => {
            setSelectedPostId(card.postId);
          }}
          onCloseAll={() => {
            if (forms.some((f) => isFormDirty(f))) {
              confirm({
                title: "Discard all posts?",
                message: (
                  <>
                    Are you sure you want to discard <b>all posts</b>? Any
                    unsaved work will be lost.
                  </>
                ),
                confirmText: "Discard all posts",
                onConfirm: () => {
                  dispatch(closeBulkCreatePostModal());
                  if (isCreation) {
                    trackEvent({
                      eventName: CREATE_POST_BULK_CANCEL,
                      metadata: { length: forms.length },
                    });
                  }
                },
              });
            } else {
              dispatch(closeBulkCreatePostModal());
            }
          }}
          onClose={(card) => {
            handleCloseForm(card.form, () => {
              if (isCreation) {
                trackEvent({ eventName: CREATE_POST_SINGLE_CANCEL });
              }
            });
          }}
          onDuplicate={(card) => {
            dispatch(
              duplicatePostFormInBulkCreate({ fromId: card.form.post._id })
            );
            toast.success("The post has been successfully duplicated.");
            trackEvent({ eventName: CREATE_POST_BULK_DUPLICATE });
          }}
          isSavingAll={isSavingAll}
          onSaveAll={() => {
            if (isSavingAll) return;
            const unsavedIds = new Set(
              forms.filter((f) => !f.formSaveData).map((f) => f.post._id)
            );
            if (unsavedIds.size === 0) return;
            savingAllTargetIdsRef.current = unsavedIds;
            setIsSavingAll(true);
            for (const [id, callbacks] of childrenCallbacksMapRef.current) {
              if (unsavedIds.has(id)) {
                callbacks.onSubmit?.("save-and-show-success-screen");
              }
            }
          }}
        />
        {forms.map((form) => {
          const handleChildCallbackRefChange = (
            vl: ChildCallbacks | undefined
          ): void => {
            if (vl) {
              childrenCallbacksMapRef.current.set(form.post._id, vl);
            } else {
              childrenCallbacksMapRef.current.delete(form.post._id);
            }
          };

          const resolveTitle = () => {
            if (form.requestFormTitle) {
              return form.requestFormTitle;
            }

            if (form.requestType === "create") {
              return "Create Post";
            }
            const postedState = getPostedState(form.post);
            if (postedState === "success") {
              return "Post Summary";
            }
            if (postedState === "failed") {
              return "Post Again";
            }
            return "Edit Post";
          };

          return (
            <div
              key={form.requestedId}
              className={`min-h-0 min-w-0 flex-1 ${selectedPostId === form.post._id ? "flex flex-col" : "hidden"}`}
            >
              {form.formSaveData ? (
                <div className="flex min-h-0 flex-1 items-center overflow-y-auto py-4">
                  <StatePostSaveSuccess form={form} />
                </div>
              ) : (
                <>
                  {form.requestType === "create" && (
                    <PostFormErrorBoundary formId={form.post._id}>
                      <CreatePost
                        form={form}
                        formTitle={resolveTitle()}
                        onChildCallbacksRefChange={handleChildCallbackRefChange}
                        onRequestClose={handleCloseForm}
                      />
                    </PostFormErrorBoundary>
                  )}
                  {form.requestType === "update" && (
                    <PostFormErrorBoundary formId={form.post._id}>
                      <EditPost
                        form={form}
                        formTitle={resolveTitle()}
                        onChildCallbacksRefChange={handleChildCallbackRefChange}
                        onRequestClose={handleCloseForm}
                      />
                    </PostFormErrorBoundary>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {confirmDialog}
    </div>
  );
};

const CreatePost = ({
  form,
  formTitle,
  onChildCallbacksRefChange,
  onRequestClose,
}: {
  form: PostFormState;
  formTitle: string;
  onChildCallbacksRefChange: (vl: ChildCallbacks | undefined) => void;
  onRequestClose: (form: PostFormState, onConfirmClose?: () => void) => void;
}) => {
  const dispatch = useDispatch();
  const { handleOnFormValueChanged } = useHandleFormDirty({ dispatch });
  const childCallbacks = useChildCallbacks({ onChildCallbacksRefChange });
  const trackEvent = useTrackEvent();

  const trackEventYT = (post: Post) => {
    const isVideoPostWithYouTube =
      post.type === "video" &&
      post.accounts?.some((el) => el.platformType === "YT");

    if (isVideoPostWithYouTube && post.meta?.format?.duration) {
      // eslint-disable-next-line unicorn/require-number-to-fixed-digits-argument
      const duration = +(+post.meta.format.duration).toFixed();
      if (duration <= 60) {
        trackEvent({
          eventName: CREATE_POST_YT,
          metadata: { duration: "< 1 min" },
        });
      } else if (duration <= 900) {
        trackEvent({
          eventName: CREATE_POST_YT,
          metadata: { duration: "1 to 15 min" },
        });
      } else if (duration > 900) {
        trackEvent({
          eventName: CREATE_POST_YT,
          metadata: { duration: "over 15 min" },
        });
      }
    }
  };

  const trackEventLI = (post: Post) => {
    if (post._id === "NEW_POST") {
      // eslint-disable-next-line unicorn/no-for-each
      post.accounts?.forEach((account) => {
        if (account.platformType === "LI") {
          trackEvent({
            eventName: CREATE_POST_LI,
            metadata: {
              pageType: account.linkedinUrn?.includes("person")
                ? "Personal Page"
                : "Company Page",
            },
          });
        }
      });
    }
  };

  const bulkTab: BulkTabContext = {
    formId: form.post._id,
    isEditing: false,
    childCallbacks,
  };
  const postForm = usePostForm(
    form.post,
    {
      async onSave(data: SaveFormResult, saveAction) {
        const { post: postForm } = data;
        await new Promise((resolve) =>
          dispatch(
            checkInstagramPostOverlapping({
              postId: postForm._id,
              accounts: postForm.accounts,
              isNow: postForm.isNow,
              when: postForm.when,
              done: resolve,
            })
          )
        );

        dispatch(
          savePostFormInBulkCreate({
            form: data,
            postSaveAction: saveAction || "save-and-close",
            requestType: form.requestType,
          })
        );

        trackEvent({ eventName: CREATE_SINGLE_POST });
        trackEventYT(postForm);
        trackEventLI(postForm);
      },
      onSuccess() {},
    },
    bulkTab
  );

  // Error state: postFormValue is undefined (data not loaded correctly)
  if (!postForm.postFormValue) {
    return <PostFormUnavailable formId={form.post._id} />;
  }

  return (
    <PostForm
      {...postForm}
      setIsFormValueChanged={(isDirty: boolean) => {
        handleOnFormValueChanged(form.post._id, isDirty);
      }}
      onCancel={() => {
        onRequestClose(form, () => {
          trackEvent({ eventName: CREATE_POST_SINGLE_CANCEL });
        });
      }}
      accountEditingIsDisabled={false}
      bulkTab={bulkTab}
      formTitle={formTitle}
    />
  );
};

const EditPost = ({
  form,
  formTitle,
  onChildCallbacksRefChange,
  onRequestClose,
}: {
  form: PostFormState;
  formTitle: string;
  onChildCallbacksRefChange: (vl: ChildCallbacks | undefined) => void;
  onRequestClose: (form: PostFormState, onConfirmClose?: () => void) => void;
}): JSX.Element => {
  const dispatch = useDispatch();
  const { handleOnFormValueChanged } = useHandleFormDirty({ dispatch });
  const childCallbacks = useChildCallbacks({ onChildCallbacksRefChange });
  return (
    <EditPostModal
      post={form.post}
      accountEditingIsDisabled={!!form.accountSelectionDisabled}
      isHidden={false}
      formTitle={formTitle}
      bulkTab={{
        formId: form.post._id,
        isEditing: true,
        onFormValueChanged: (isDirty) => {
          handleOnFormValueChanged(form.post._id, isDirty);
        },
        onSave: async (saveResult, saveAction) => {
          dispatch(
            savePostFormInBulkCreate({
              form: saveResult,
              postSaveAction: saveAction || "save-and-close",
              requestType: form.requestType,
            })
          );
        },
        onCancel: () => {
          onRequestClose(form);
        },
        childCallbacks,
      }}
    />
  );
};

const ModalContainer = (props: {
  title: string;
  isLoading: boolean;
  isHidden: boolean;
  onCancel: () => void;
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <CreatePostModalRoot
      show={!props.isHidden}
      onHide={props.onCancel}
      animation={false}
      bsStyle="giselle"
      enforceFocus={false}
      autoFocus={false}
      keyboard={false}
      contentClassName="p-0 gap-0"
      className={props.className}
      modal={false}
    >
      <Modal.Title className="sr-only">{props.title}</Modal.Title>
      <div data-testid="bulk-create-post-modal">
        <ChildrenModalFixScrollStyles />
        <Modal.Body className="h-full min-h-0 overflow-hidden">
          {props.isLoading && <FullPageLoading />}
          {props.children}
        </Modal.Body>
      </div>
    </CreatePostModalRoot>
  );
};

const useSelectedPostId = () => {
  const dispatch = useDispatch();
  const id = useBulkState().selectedPostId;
  return {
    selectedPostId: id,
    setSelectedPostId: (postId: string | undefined) => {
      dispatch(selectedPostIdSlice.actions.set(postId));
    },
  };
};

const CreatePostModalRoot = styled(Modal)<{ bsStyle?: string }>`
  overflow: hidden;

  .modal-dialog,
  .modal-content,
  [data-testid="bulk-create-post-modal"],
  .modal-body {
    min-height: 0;
    height: 100%;
    overflow: hidden;
  }

  .modal-body {
    padding: 0;
    margin-bottom: 0;
  }
`;

const ChildrenModalFixScrollStyles = createGlobalStyle`
  .modal {
    overflow-y: auto;
  }
`;

/**
 * Error boundary for post form components.
 * Catches React render errors and displays a user-friendly error UI.
 * Also logs errors to console for debugging.
 */
class PostFormErrorBoundary extends React.Component<
  { children: React.ReactNode; formId: string },
  { hasError: boolean; error: Error | null }
> {
  state = { hasError: false, error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[PostFormError]", {
      formId: this.props.formId,
      error: error.message,
      stack: error.stack,
      componentStack: info.componentStack,
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-96 text-center p-8">
          <div className="text-red-500 mb-4">
            <i className="fa-solid fa-triangle-exclamation text-4xl"></i>
          </div>
          <h3 className="text-lg font-semibold text-neutral-900 mb-2">
            Unable to load post form
          </h3>
          <p className="text-sm text-neutral-600 mb-4">
            {this.state.error?.message || "An unexpected error occurred"}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="text-sm text-blue-500 hover:underline"
          >
            Refresh page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Error state UI for when post form data is unavailable.
 * Shown when postFormValue is undefined (data loading/restoration failed).
 */
const PostFormUnavailable = ({ formId }: { formId: string }) => {
  console.error("[PostFormUnavailable] Form data missing:", { formId });
  return (
    <div className="flex flex-col items-center justify-center h-96 text-center p-8">
      <div className="text-amber-500 mb-4">
        <i className="fa-solid fa-circle-exclamation text-4xl"></i>
      </div>
      <h3 className="text-lg font-semibold text-neutral-900 mb-2">
        Post data unavailable
      </h3>
      <p className="text-sm text-neutral-600">
        Unable to load form data. The draft may have been created with different
        settings.
      </p>
    </div>
  );
};
